/**
 * Cliente HTTP do ajuste manual de LR pelo perito na mesa (E33.F2 / ADR-0044). // ux-neutrality:allow doc-comment
 *
 * Espelha `apps/api/runtime/case_workspace.py` (PATCH/DELETE) e // ux-neutrality:allow doc-comment
 * `apps/api/types/lr_adjustment.py`. O perito digita o LR efetivo de um critério // ux-neutrality:allow doc-comment
 * (`scope='criterion'`) ou do total (`scope='composite'`); DELETE reverte ao // ux-neutrality:allow doc-comment
 * valor do motor. Sem teto (ADR-0044), só sanidade `[LR_MIN, LR_MAX]`. // ux-neutrality:allow doc-comment
 */
import axios, { AxiosError, type AxiosInstance } from 'axios';
import type { LrAdjustmentStateData } from '@/lib/case_workspace';

// Sanidade numérica — espelha LR_MIN/LR_MAX de apps/api/types/lr_adjustment.py.
export const LR_MIN = 1e-6;
export const LR_MAX = 1e6;

export type LrAdjustmentScope = 'criterion' | 'composite';

export interface LrAdjustmentRequestData {
  scope: LrAdjustmentScope;
  criterion_key?: string | null;
  value: number;
  justification?: string | null;
}

export type LrAdjustmentErrorReason =
  | 'not_case_perito'
  | 'analysis_not_found'
  | 'unknown_criterion'
  | 'criterion_key_required'
  | 'invalid_value'
  | 'network'
  | 'unknown';

export class LrAdjustmentError extends Error {
  public readonly reason: LrAdjustmentErrorReason;

  constructor(reason: LrAdjustmentErrorReason, message?: string) {
    super(message ?? reason);
    this.reason = reason;
    this.name = 'LrAdjustmentError';
  }
}

const HTTP_FORBIDDEN = 403;
const HTTP_NOT_FOUND = 404;
const HTTP_UNPROCESSABLE = 422;

function isNetworkError(err: AxiosError): boolean {
  return err.code === 'ERR_NETWORK' || err.code === 'ECONNABORTED';
}

function detailString(err: AxiosError): string {
  const data = err.response?.data as { detail?: unknown } | undefined;
  // 422 do pydantic chega como lista de erros; o nosso detail de domínio é string.
  return typeof data?.detail === 'string' ? data.detail : '';
}

export function classifyLrAdjustmentError(err: unknown): LrAdjustmentError {
  if (!axios.isAxiosError(err)) return new LrAdjustmentError('unknown');
  const axiosErr = err as AxiosError;
  const httpStatus = axiosErr.response?.status;
  const detail = detailString(axiosErr);
  if (httpStatus === HTTP_FORBIDDEN) return new LrAdjustmentError('not_case_perito');
  if (httpStatus === HTTP_NOT_FOUND) return new LrAdjustmentError('analysis_not_found');
  if (httpStatus === HTTP_UNPROCESSABLE) {
    if (detail === 'UNKNOWN_CRITERION') return new LrAdjustmentError('unknown_criterion');
    if (detail === 'CRITERION_KEY_REQUIRED') return new LrAdjustmentError('criterion_key_required');
    // Demais 422 (validação do schema: value fora de [LR_MIN, LR_MAX], etc.).
    return new LrAdjustmentError('invalid_value');
  }
  if (isNetworkError(axiosErr)) return new LrAdjustmentError('network');
  return new LrAdjustmentError('unknown');
}

/** PATCH: registra um override do LR efetivo de um critério ou do total. */
export async function adjustLr(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  request: LrAdjustmentRequestData,
): Promise<LrAdjustmentStateData> {
  try {
    const { data } = await client.patch<LrAdjustmentStateData>(
      `/casos/${caseId}/analyses/${analysisId}/lr`,
      request,
    );
    return data;
  } catch (err) {
    throw classifyLrAdjustmentError(err);
  }
}

/** DELETE: reverte o override ao valor calculado pelo motor (append `cleared`). */
export async function clearLr(
  client: AxiosInstance,
  caseId: string,
  analysisId: string,
  scope: LrAdjustmentScope,
  criterionKey?: string | null,
): Promise<LrAdjustmentStateData> {
  try {
    const { data } = await client.delete<LrAdjustmentStateData>(
      `/casos/${caseId}/analyses/${analysisId}/lr`,
      { params: { scope, ...(criterionKey ? { criterion_key: criterionKey } : {}) } },
    );
    return data;
  } catch (err) {
    throw classifyLrAdjustmentError(err);
  }
}
